import { call, put, select, takeLatest, fork, all } from 'redux-saga/effects';
import axios from 'axios';
import { SagaIterator } from 'redux-saga';

import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';
import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';
import { setSubmitHasFailed } from 'services/virtualizedTable/actions';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';

import {
    RequestedCreateFertilizerProductAction,
    RequestedDeleteFertilizerProductAction,
    RequestedUpdateFertilizerProductAction
} from './types';
import {
    succeededCreateFertilizerProduct,
    failedCreateFertilizerProduct,
    setFertilizerProduct,
    succeededUpdateFertilizerProduct,
    failedUpdateFertilizerProduct,
    succeededDeleteFertilizerProduct,
    failedDeleteFertilizerProduct
} from './actions';
import {
    FERTILIZER_PRODUCT_CREATE_NAMESPACE,
    FERTILIZER_PRODUCT_CREATE_REQUESTED,
    FERTILIZER_PRODUCT_DELETE_NAMESPACE,
    FERTILIZER_PRODUCT_DELETE_REQUESTED,
    FERTILIZER_PRODUCT_UPDATE_NAMESPACE,
    FERTILIZER_PRODUCT_UPDATE_REQUESTED
} from './constants';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postProduct(
    action: RequestedCreateFertilizerProductAction
): SagaIterator {
    const organizationId = yield select(selectCurrentOrganizationId);
    const idToken = yield select(idTokenSelector);
    const url = `${REACT_APP_API_URL}/v1/FertilizerProducts`;

    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: {
                ...action.data,
                organizationId
            }
        });
        yield put(succeededCreateFertilizerProduct(response));
        yield put(setFertilizerProduct(response.data));
    } catch (error) {
        yield put(failedCreateFertilizerProduct(error));
        yield put(setSubmitHasFailed(true));
        yield put(
            updateQueryString({
                [ERROR_MODAL_QUERY_STRING_PARAM]:
                    FERTILIZER_PRODUCT_CREATE_NAMESPACE
            })
        );
    }
}

export function* watchCreateProductCreateRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_PRODUCT_CREATE_REQUESTED, postProduct);
}

export function* patchProduct(
    action: RequestedUpdateFertilizerProductAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id, product } = action.payload;
    const url = `${REACT_APP_API_URL}/v1/FertilizerProducts/${id}`;

    try {
        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: {
                ...product
            }
        });
        yield put(succeededUpdateFertilizerProduct(response));
        yield put(setFertilizerProduct(response.data));
    } catch (error) {
        yield put(failedUpdateFertilizerProduct(error));
        yield put(setSubmitHasFailed(true));
        yield put(
            updateQueryString({
                [ERROR_MODAL_QUERY_STRING_PARAM]:
                    FERTILIZER_PRODUCT_UPDATE_NAMESPACE
            })
        );
    }
}

export function* watchUpdateProductRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_PRODUCT_UPDATE_REQUESTED, patchProduct);
}

export function* deleteProduct(
    action: RequestedDeleteFertilizerProductAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id, uuid } = action.payload;
    const url = `${REACT_APP_API_URL}/v1/FertilizerProducts/${id}`;

    try {
        yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });
        const res = yield call(axios, url, {
            method: 'get',
            headers: {
                Authorization: idToken
            }
        });
        yield put(succeededDeleteFertilizerProduct(uuid));
        yield put(setFertilizerProduct(res.data));
    } catch (error) {
        yield put(failedDeleteFertilizerProduct(error));
        yield put(setSubmitHasFailed(true));
        yield put(
            updateQueryString({
                [ERROR_MODAL_QUERY_STRING_PARAM]:
                    FERTILIZER_PRODUCT_DELETE_NAMESPACE
            })
        );
    }
}

export function* watchDeleteProductRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_PRODUCT_DELETE_REQUESTED, deleteProduct);
}

export default function* productsSagas(): SagaIterator {
    yield all([
        fork(watchCreateProductCreateRequested),
        fork(watchUpdateProductRequested),
        fork(watchDeleteProductRequested)
    ]);
}
